import { useEffect } from "react";

import { setDataTable } from "./store";
import {
    PERIPHERAL_TYPE_CASH_MANAGEMENT,
    PERIPHERAL_TYPE_MONETIC,
} from "@constants/index";

export type AppRecord = Record<string, unknown>;

export type PeripheralsInUseMap = Record<
    string,
    {
        monetics?: string[];
        cash_management?: string[];
    }
>;

// Contract: `peripheralsInUse` MUST be provided (non-undefined) by the
// consumer. The fallback decision when the BE payload omits the field is
// pushed to the call site so a missing-from-payload case is visible at
// the point of consumption (typically `data?.othersData?.peripheralsInUse
// ?? {}`) rather than hidden as a silent degradation inside the hook.
type Params = {
    localApps: AppRecord[];
    peripheralsInUse: PeripheralsInUseMap;
    oneShop: boolean;
};

export function buildOppositeEntries(
    map: PeripheralsInUseMap | undefined,
    oneShop: boolean
): AppRecord[] {
    if (!map) return [];

    return Object.entries(map).map(([shopId, ids]) => {
        const peripherals = [
            ...(ids.monetics ?? []).map((id) => ({
                peripheralType: PERIPHERAL_TYPE_MONETIC,
                id_monetic: id,
                shopId,
            })),
            ...(ids.cash_management ?? []).map((id) => ({
                peripheralType: PERIPHERAL_TYPE_CASH_MANAGEMENT,
                peripheralId: id,
                shopId,
            })),
        ];

        return {
            shopId,
            subRows: oneShop ? peripherals : [{ subRows: peripherals }],
        };
    });
}

export function useMergedAppsForMoneticStore({
    localApps,
    peripheralsInUse,
    oneShop,
}: Params) {
    useEffect(() => {
        const oppositeEntries = buildOppositeEntries(peripheralsInUse, oneShop);
        setDataTable([...localApps, ...oppositeEntries]);
    }, [localApps, peripheralsInUse, oneShop]);
}
